<script setup lang="ts">
import type { DateValue } from 'reka-ui';
import {
  RangeCalendarCell,
  RangeCalendarCellTrigger,
  RangeCalendarGrid,
  RangeCalendarGridBody,
  RangeCalendarGridHead,
  RangeCalendarGridRow,
  RangeCalendarHeadCell,
  RangeCalendarHeader,
  RangeCalendarNext,
  RangeCalendarPrev,
} from 'reka-ui';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { DateFormatter, getLocalTimeZone } from '@internationalized/date';

defineProps<{
  month: { value: DateValue; rows: DateValue[][] };
  weekDays: string[];
  vertical?: boolean;
  hideNav?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  ghostEnd?: DateValue;
  ghostStart?: DateValue;
  crescentStart?: boolean;
  directFlightDates?: string[];
}>();

const { t } = useI18n();
const fullLocale = useFullLocale();
const df = computed(() => new DateFormatter(fullLocale.value, { month: 'long', year: 'numeric' }));
const directFlightHintId = useId();

const cellClass = cn(
  'relative p-0 text-center text-sm transition-all duration-150 focus-within:relative focus-within:z-20',
  '[&:has([data-disabled])]:cursor-default',
  // in-range highlight
  '[&:has([data-highlighted])]:bg-brand-primary-50',
  'first:[&:has([data-highlighted])]:rounded-l-full last:[&:has([data-highlighted])]:rounded-r-full',
  '[&:has([data-highlighted-start])]:rounded-l-full [&:has([data-highlighted-end])]:rounded-r-full',
  // committed range
  '[&:has([data-selected])]:bg-brand-primary-50',
  'first:[&:has([data-selected])]:rounded-l-full last:[&:has([data-selected])]:rounded-r-full',
  '[&:has([data-selection-end])]:rounded-r-full',
  '[&:has([data-selection-start]):not(:has([data-highlighted-end]))]:rounded-l-full',
  // ghost-start: inverted anchor — clear highlight on cells that moved out of range
  '[.group:has([data-ghost-start]):has([data-selection-start][data-highlighted-start])_&:has([data-highlighted]):not(:has([data-selection-start]))]:bg-transparent',
  '[.group:has([data-ghost-start]):has([data-selection-start][data-highlighted-start])_&:has([data-selection-start])]:rounded-r-full',
  // no hover: pill shape on lone start date (no crescent)
  '[.group:not(:has([data-highlighted])):not(:has([data-selection-end])):not(:has([data-crescent-start]))_&:has([data-selection-start])]:rounded-r-full',
  // crescent: background and rounding on the right-neighbor cell
  '[.group:not(:has([data-highlighted])):not(:has([data-selection-end]))_[data-crescent-start]_td:has([data-selection-start])+&]:bg-brand-primary-50',
  '[.group:not(:has([data-highlighted])):not(:has([data-selection-end]))_[data-crescent-start]_td:has([data-selection-start])+&]:rounded-l-none',
  '[.group:not(:has([data-highlighted])):not(:has([data-selection-end]))_[data-crescent-start]_td:has([data-selection-start])+&]:rounded-r-full',
);

const triggerClass = cn(
  'grid h-11 w-11 grid-rows-3 place-items-center rounded-full p-0 text-base font-medium text-gray-700 lg:h-10 lg:w-10 lg:text-sm',
  'transition-colors duration-150 hover:bg-gray-100 focus-visible:outline-none',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[outside-view]:opacity-0',
  // in-range fill
  'data-[selected]:bg-brand-primary-100',
  'data-[highlighted]:hover:bg-brand-primary-200',
  '[&[data-highlighted]:not([data-highlighted-end])]:bg-brand-primary-100',
  // selection endpoints — full brand blue
  '[&[data-selected][data-selection-start]]:bg-brand-primary-600 [&[data-selected][data-selection-start]]:text-white [&[data-selected][data-selection-start]]:hover:bg-brand-primary-600',
  '[&[data-selected][data-selection-end]]:bg-brand-primary-600 [&[data-selected][data-selection-end]]:text-white [&[data-selected][data-selection-end]]:hover:bg-brand-primary-600',
  '[&[data-highlighted-end]]:bg-brand-primary-600 [&[data-highlighted-end]]:text-white [&[data-highlighted-end]]:hover:bg-brand-primary-600',
  '[&[data-highlighted-start][data-highlighted]:not([data-highlighted-end])]:bg-brand-primary-600 [&[data-highlighted-start][data-highlighted]:not([data-highlighted-end])]:hover:bg-brand-primary-600 [&[data-highlighted-start]]:text-white',
  // ghost markers — visual anchor for existing endpoints during re-selection
  '[&[data-ghost-start]]:bg-brand-primary-600 [&[data-ghost-start]]:text-white [&[data-ghost-start]]:hover:bg-brand-primary-600',
  '[&[data-ghost-start][data-highlighted]:not([data-highlighted-start])]:bg-brand-primary-100 [&[data-ghost-start][data-highlighted]:not([data-highlighted-start])]:text-gray-700 [&[data-ghost-start][data-highlighted]:not([data-highlighted-start])]:hover:bg-brand-primary-200',
  '[&[data-ghost-end]]:bg-brand-primary-600 [&[data-ghost-end]]:text-white [&[data-ghost-end]]:hover:bg-brand-primary-600',
  '[&[data-ghost-end][data-highlighted]:not([data-highlighted-end])]:bg-brand-primary-100 [&[data-ghost-end][data-highlighted]:not([data-highlighted-end])]:text-gray-700 [&[data-ghost-end][data-highlighted]:not([data-highlighted-end])]:hover:bg-brand-primary-200',
  // ghost-start: inverted anchor — clear hover range when dragging left past existing start
  '[.group:has([data-ghost-start]):has([data-selection-start][data-highlighted-start])_&[data-highlighted]:not([data-selection-start])]:bg-transparent',
  '[.group:has([data-ghost-start]):has([data-selection-start][data-highlighted-start])_&[data-highlighted]:not([data-selection-start])]:text-gray-700',
  '[.group:has([data-ghost-start]):has([data-selection-start][data-highlighted-start])_&[data-highlighted]:not([data-selection-start])]:hover:bg-gray-100',
  // crescent: background pill on right-neighbor
  '[.group:not(:has([data-highlighted])):not(:has([data-selection-end]))_[data-crescent-start]_td:has([data-selection-start])+td_&]:bg-background',
  '[.group:not(:has([data-highlighted])):not(:has([data-selection-end]))_[data-crescent-start]_td:has([data-selection-start])+td_&]:hover:bg-background',
);

const dotClass = cn(
  'row-start-3',
  // one mirror per text-white rule of triggerClass: same selectors, so the same precedence
  '[[data-selected][data-selection-start]_&]:bg-white',
  '[[data-selected][data-selection-end]_&]:bg-white',
  '[[data-highlighted-end]_&]:bg-white',
  '[[data-highlighted-start]_&]:bg-white',
  '[[data-ghost-start]_&]:bg-white',
  '[[data-ghost-start][data-highlighted]:not([data-highlighted-start])_&]:bg-brand-primary-700',
  '[[data-ghost-end]_&]:bg-white',
  '[[data-ghost-end][data-highlighted]:not([data-highlighted-end])_&]:bg-brand-primary-700',
  '[.group:has([data-ghost-start]):has([data-selection-start][data-highlighted-start])_[data-highlighted]:not([data-selection-start])_&]:bg-brand-primary-700',
);
</script>

<template>
  <div
    :class="cn('flex flex-col gap-3', !vertical && 'p-3 md:px-6 md:py-5', vertical && 'w-fit')"
    :data-crescent-start="crescentStart ? '' : undefined"
    :data-calendar-month="`${month.value.year}-${month.value.month}`"
  >
    <RangeCalendarHeader class="flex items-center">
      <RangeCalendarPrev
        v-if="isFirst && !hideNav"
        :aria-label="t('components.common.previousMonth')"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-transparent hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50"
      >
        <ChevronLeft
          class="h-5 w-5 text-gray-500"
          aria-hidden="true"
        />
      </RangeCalendarPrev>

      <span
        :class="cn('flex-1 text-center text-base capitalize md:text-sm', vertical ? 'font-semibold' : 'font-medium')"
      >
        {{ df.format(month.value.toDate(getLocalTimeZone())) }}
      </span>

      <RangeCalendarNext
        v-if="isLast && !hideNav"
        :aria-label="t('components.common.nextMonth')"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-transparent hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50"
      >
        <ChevronRight
          class="h-5 w-5 text-gray-500"
          aria-hidden="true"
        />
      </RangeCalendarNext>
    </RangeCalendarHeader>

    <RangeCalendarGrid>
      <RangeCalendarGridHead>
        <RangeCalendarGridRow class="flex">
          <RangeCalendarHeadCell
            v-for="day in weekDays"
            :key="day"
            class="w-11 text-center text-base font-medium text-gray-700 lg:w-10 lg:text-sm"
          >
            {{ day }}
          </RangeCalendarHeadCell>
        </RangeCalendarGridRow>
      </RangeCalendarGridHead>
      <RangeCalendarGridBody class="cursor-pointer">
        <RangeCalendarGridRow
          v-for="(week, i) in month.rows"
          :key="i"
          class="mt-1 flex"
        >
          <RangeCalendarCell
            v-for="date in week"
            :key="date.toString()"
            :date="date"
            :aria-hidden="date.month !== month.value.month || date.year !== month.value.year ? 'true' : undefined"
            :class="cellClass"
          >
            <RangeCalendarCellTrigger
              :day="date"
              :month="month.value"
              :data-ghost-end="ghostEnd && date.compare(ghostEnd) === 0 ? '' : undefined"
              :data-ghost-start="ghostStart && date.compare(ghostStart) === 0 ? '' : undefined"
              :data-date="date.toString()"
              :class="triggerClass"
              :aria-describedby="directFlightDates?.includes(date.toString()) ? directFlightHintId : undefined"
            >
              <template #default="{ dayValue }">
                <span class="row-start-2">{{ dayValue }}</span>
                <UiCustomRangeCalendarDirectFlightDot
                  v-if="directFlightDates?.includes(date.toString())"
                  :class="dotClass"
                />
              </template>
            </RangeCalendarCellTrigger>
          </RangeCalendarCell>
        </RangeCalendarGridRow>
      </RangeCalendarGridBody>
    </RangeCalendarGrid>
    <!-- reka's aria-label replaces the cell content for a screen reader: the dot is told here -->
    <span
      :id="directFlightHintId"
      class="sr-only"
    >
      {{ t('components.search.datepicker.directFlightLegend') }}
    </span>
  </div>
</template>
